<template>
  <Teleport to="body">
    <Transition name="sm-fade">
      <div v-if="screenMirrorOpen" class="sm-overlay" @mousedown.self="close">
        <div class="sm-box" :class="{ 'sm-box--watch': step === 'watch' }" role="dialog" aria-label="Espelho ao vivo">

          <!-- Escolher o agente -->
          <template v-if="step === 'pick'">
            <div class="sm-head">
              <span class="sm-head-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><circle cx="12" cy="10" r="2.5"/></svg>
              </span>
              <div class="sm-head-text">
                <strong>Espelho ao vivo</strong>
                <span>Veja ao vivo a tela do sistema de um agente</span>
              </div>
              <kbd class="sm-kbd">Ctrl @</kbd>
              <button type="button" class="sm-icon-btn" title="Fechar (Esc)" @click="close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div class="sm-search-row">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                ref="searchRef"
                v-model="query"
                class="sm-search"
                placeholder="Buscar agente pelo nome ou e-mail..."
                autocomplete="off"
                spellcheck="false"
                @keydown.down.prevent="moveFocus(1)"
                @keydown.up.prevent="moveFocus(-1)"
                @keydown.enter.prevent="filtered[focused] && start(filtered[focused])"
              />
            </div>

            <div class="sm-list">
              <div v-if="loadingAgents" class="sm-state"><span class="sm-spinner"></span> Carregando agentes...</div>
              <div v-else-if="!filtered.length" class="sm-state">Nenhum agente encontrado.</div>
              <button
                v-for="(a, i) in filtered"
                v-else
                :key="a.id"
                type="button"
                class="sm-agent"
                :class="{ 'sm-agent--focus': focused === i }"
                :disabled="starting"
                @mouseenter="focused = i"
                @click="start(a)"
              >
                <span class="sm-avatar">
                  <img v-if="avatarSrc(a)" :src="avatarSrc(a)!" alt="" @error="brokenAvatars.add(a.id)" />
                  <template v-else>{{ initials(a) }}</template>
                  <span class="sm-dot" :class="isOnline(a) ? 'sm-dot--on' : 'sm-dot--off'"></span>
                </span>
                <span class="sm-agent-body">
                  <span class="sm-agent-name">{{ fullName(a) }}</span>
                  <span class="sm-agent-sub">{{ a.email }}</span>
                </span>
                <span class="sm-agent-status" :class="{ 'sm-agent-status--on': isOnline(a) }">{{ isOnline(a) ? 'Online' : 'Offline' }}</span>
              </button>
            </div>

            <p v-if="error" class="sm-error">{{ error }}</p>
            <div class="sm-foot">
              Só a aba do WootDash é mostrada, nunca outras janelas. O agente vê um aviso enquanto você assiste, e a sessão fica registrada na auditoria.
            </div>
          </template>

          <!-- Assistindo -->
          <template v-else>
            <div class="sm-head sm-head--watch">
              <button type="button" class="sm-back" title="Trocar agente" @click="switchAgent">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                Trocar
              </button>
              <div class="sm-head-text">
                <strong>{{ target ? fullName(target) : '' }}</strong>
                <span>
                  <span class="sm-live" :class="'sm-live--' + liveState">{{ STATE_LABEL[liveState] }}</span>
                  <template v-if="activityLabel"> · {{ activityLabel }}</template>
                </span>
              </div>
              <div class="sm-actions">
                <div class="sm-tools" role="toolbar" aria-label="Marcar a tela do agente">
                  <button v-for="t in TOOLS" :key="t.id" type="button" class="sm-tool" :class="{ 'is-on': tool === t.id }" :disabled="!hasPlayer"
                          :title="t.title" :aria-pressed="tool === t.id" @click="pickTool(t.id)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" v-html="t.icon"></svg>
                  </button>
                  <span class="sm-tools-sep"></span>
                  <button v-for="c in MARK_COLORS" :key="c" type="button" class="sm-color" :class="{ 'is-on': color === c }" :style="{ background: c }"
                          :title="'Cor'" :aria-pressed="color === c" @click="color = c"></button>
                  <span class="sm-tools-sep"></span>
                  <button type="button" class="sm-tool" :disabled="!myMarks.length" title="Desfazer a última marcação" @click="undoMark">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-15-6.7L3 13"/></svg>
                  </button>
                  <button type="button" class="sm-tool" :disabled="!hasPlayer" title="Limpar todas as marcações" @click="clearMarks">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/></svg>
                  </button>
                </div>
                <button type="button" class="sm-btn" title="Pedir a tela inteira de novo" @click="ping(true)">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  Atualizar
                </button>
                <button type="button" class="sm-btn" :disabled="!hasPlayer || printing" title="Baixar uma imagem da tela agora" @click="takePrint">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  {{ printing ? 'Gerando...' : 'Print' }}
                </button>
                <button type="button" class="sm-btn" title="Tela cheia" @click="toggleFullscreen">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
                </button>
                <button type="button" class="sm-btn sm-btn--stop" title="Parar e fechar (Esc)" @click="close">Parar</button>
              </div>
            </div>

            <div ref="stageRef" class="sm-stage">
              <div class="sm-board">
                <div ref="playerRoot" class="sm-player"></div>
                <canvas
                  v-show="hasPlayer" ref="markCanvas" class="sm-marks" :class="['sm-marks--' + tool]"
                  @pointerdown="onMarkDown" @pointermove="onMarkMove" @pointerup="onMarkUp" @pointercancel="onMarkUp" @pointerleave="onMarkLeave"
                ></canvas>
                <textarea
                  v-if="textDraft" ref="textInput" v-model="textDraft.value" class="sm-text-input" :maxlength="MAX_TEXT_LENGTH" rows="2"
                  :style="{ left: textDraft.left + 'px', top: textDraft.top + 'px', borderColor: color }" placeholder="Escreva e tecle Enter"
                  @keydown.enter.exact.prevent="commitText" @keydown.esc.stop.prevent="textDraft = null" @blur="commitText"
                ></textarea>
              </div>
              <div v-if="hasPlayer && (agentHidMarks || marksNotice)" class="sm-marks-notice">{{ agentHidMarks ? 'O agente escondeu as marcações — ele não está vendo o que você marcar.' : marksNotice }}</div>
              <div v-if="!hasPlayer" class="sm-stage-msg">
                <template v-if="liveState === 'hidden'">
                  <strong>A aba do sistema não está visível agora</strong>
                  <span>Quando o agente voltar pra aba do WootDash, a tela aparece aqui.</span>
                </template>
                <template v-else-if="liveState === 'ended'">
                  <strong>O agente saiu do sistema</strong>
                  <span>Escolha outro agente ou espere ele entrar de novo.</span>
                </template>
                <template v-else>
                  <span class="sm-spinner sm-spinner--big"></span>
                  <strong>Esperando a tela de {{ target?.firstName }}…</strong>
                  <span>Ele precisa estar com o WootDash aberto. Se estiver offline, a tela aparece assim que ele entrar.</span>
                </template>
              </div>
              <div v-else-if="liveState !== 'live'" class="sm-stage-banner">
                {{ liveState === 'hidden' ? 'O agente saiu da aba do sistema — mostrando a última imagem.' : 'O agente saiu do sistema — mostrando a última imagem.' }}
              </div>
            </div>
            <p v-if="error" class="sm-error sm-error--watch">{{ error }}</p>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
// Espelho ao vivo (Ctrl+@) — lado do SUPERVISOR. Escolhe o agente, pede a sessão pelo hub (IniciarEspelho: checa a
// permissão agents.screen_view e grava na auditoria) e toca ao vivo, com o Replayer do rrweb, os quadros que a aba do
// agente manda (useScreenMirror.ts). ManterEspelho a cada 10 s mantém a sessão viva — sem ele a aba do agente para.
import { ref, reactive, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { toMediaUrl } from '@/utils/media'
import { useStore } from 'vuex'
import { Replayer, EventType, type eventWithTime } from 'rrweb'
import 'rrweb/dist/style.css'
import html2canvas from 'html2canvas'
import { listAgents, type CpAgent } from '@/services/wootdashAgents'
import { invokeHub, onHubEvent } from '@/composables/useConversationRealtime'
import { screenMirrorOpen, unpackMirrorEvents } from '@/composables/useScreenMirror'
import { MarkBoard, drawMarks, fitCanvas, newMarkId, MARK_COLORS, MAX_TEXT_LENGTH, type MarkMessage, type Point } from '@/utils/mirrorMarks'

type LiveState = 'waiting' | 'live' | 'hidden' | 'ended'
interface StartResult { success: boolean; failure: number | string; sessionId: string | null; agentName: string | null }
interface MirrorChunk { sessionId: string; agentId: string; tabId: string; seq: number; index: number; total: number; chunk: string }
interface MirrorState { sessionId: string; agentId: string; tabId: string; state: 'visivel' | 'oculta' | 'encerrado' | 'marcacoes_limpas' | 'marcacoes_ocultas' | 'marcacoes_visiveis' }

const STATE_LABEL: Record<LiveState, string> = {
  waiting: 'Esperando a tela',
  live: 'Ao vivo',
  hidden: 'Fora da aba do sistema',
  ended: 'Saiu do sistema',
}
const FAILURE_LABEL: Record<string, string> = {
  Forbidden: 'Seu perfil não tem a permissão "Ver tela de agente". Peça a um administrador em Perfis.',
  AgentNotFound: 'Agente não encontrado ou inativo.',
  Self: 'Escolha outro agente — esta é a sua própria tela.',
}
const FAILURE_BY_NUMBER = ['None', 'Forbidden', 'AgentNotFound', 'Self']

const store = useStore()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')
const myId = computed<string>(() => (store.getters.authUser?.wootdash_agent_id ?? '').toLowerCase())

const step = ref<'pick' | 'watch'>('pick')
const agents = ref<CpAgent[]>([])
const loadingAgents = ref(false)
const query = ref('')
const focused = ref(0)
const error = ref('')
const starting = ref(false)
const searchRef = ref<HTMLInputElement | null>(null)

const target = ref<CpAgent | null>(null)
const sessionId = ref<string | null>(null)
const liveState = ref<LiveState>('waiting')
const lastActivity = ref<number | null>(null)
const nowTick = ref(Date.now())
const hasPlayer = ref(false)
const printing = ref(false)
const stageRef = ref<HTMLElement | null>(null)
const playerRoot = ref<HTMLElement | null>(null)

let replayer: Replayer | null = null
let currentTab: string | null = null
let offset = 0
let viewport = { width: 0, height: 0 }
const pending = new Map<string, { parts: (string | undefined)[]; got: number }>()
let keepAliveTimer: ReturnType<typeof setInterval> | undefined
let tickTimer: ReturnType<typeof setInterval> | undefined
let firstFrameTimer: ReturnType<typeof setTimeout> | undefined
let resizeObserver: ResizeObserver | undefined

const fullName = (a: CpAgent) => `${a.firstName} ${a.lastName ?? ''}`.trim()
// avatarUrl vem cru da API (caminho relativo de POST /anexos) — toMediaUrl monta a URL exibível. Foto que não
// carrega cai pras iniciais.
const brokenAvatars = reactive(new Set<string>())
const avatarSrc = (a: CpAgent): string | null => (brokenAvatars.has(a.id) ? null : toMediaUrl(a.avatarUrl))
const initials = (a: CpAgent) => `${a.firstName?.[0] ?? ''}${a.lastName?.[0] ?? ''}`.toUpperCase() || '?'
const isOnline = (a: CpAgent) => (a.status ?? '').toLowerCase() === 'online'

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase()
  return agents.value
    .filter(a => a.isActive && a.id.toLowerCase() !== myId.value)
    .filter(a => !q || fullName(a).toLowerCase().includes(q) || (a.email ?? '').toLowerCase().includes(q))
    .sort((a, b) => Number(isOnline(b)) - Number(isOnline(a)) || fullName(a).localeCompare(fullName(b), 'pt-BR'))
})
watch(query, () => { focused.value = 0 })

const activityLabel = computed(() => {
  if (!lastActivity.value) return ''
  const s = Math.max(0, Math.round((nowTick.value - lastActivity.value) / 1000))
  return s < 3 ? 'atividade agora' : s < 60 ? `atividade há ${s}s` : `atividade há ${Math.round(s / 60)} min`
})

function moveFocus(delta: number) {
  const n = filtered.value.length
  if (n) focused.value = (focused.value + delta + n) % n
}

async function loadAgents() {
  loadingAgents.value = true
  try {
    agents.value = (await listAgents(token.value)).agents
  } catch {
    error.value = 'Não deu pra carregar os agentes.'
  } finally {
    loadingAgents.value = false
  }
}

watch(screenMirrorOpen, async open => {
  if (open) {
    step.value = 'pick'
    error.value = ''
    query.value = ''
    await nextTick()
    searchRef.value?.focus()
    await loadAgents()
  } else {
    await stop()
  }
})

// ── sessão ──────────────────────────────────────────────────────────────────────────────────

function failureText(failure: number | string): string {
  const key = typeof failure === 'number' ? FAILURE_BY_NUMBER[failure] : failure
  return FAILURE_LABEL[key ?? ''] ?? 'Não deu pra iniciar o espelho.'
}

async function start(agent: CpAgent) {
  if (starting.value) return
  starting.value = true
  error.value = ''
  try {
    const result = await invokeHub<StartResult>('IniciarEspelho', agent.id)
    if (!result.success || !result.sessionId) {
      error.value = failureText(result.failure)
      return
    }
    target.value = agent
    sessionId.value = result.sessionId
    liveState.value = 'waiting'
    lastActivity.value = null
    step.value = 'watch'
    keepAliveTimer = setInterval(() => ping(false), 10_000)
    tickTimer = setInterval(() => { nowTick.value = Date.now() }, 1_000)
    // A aba do agente não respondeu (recarregando, ou o "iniciar" se perdeu): pede a foto inteira de novo.
    firstFrameTimer = setTimeout(() => { if (!replayer) ping(true) }, 4_000)
    await nextTick()
    if (stageRef.value) {
      resizeObserver = new ResizeObserver(() => fit())
      resizeObserver.observe(stageRef.value)
    }
  } catch (e: any) {
    error.value = e?.message || 'Não deu pra iniciar o espelho.'
  } finally {
    starting.value = false
  }
}

function ping(fullSnapshot: boolean) {
  if (!target.value || !sessionId.value) return
  invokeHub('ManterEspelho', target.value.id, sessionId.value, fullSnapshot).catch(() => { /* reconecta e tenta no próximo */ })
}

async function stop() {
  resetMarks()
  if (keepAliveTimer) clearInterval(keepAliveTimer)
  if (tickTimer) clearInterval(tickTimer)
  if (firstFrameTimer) clearTimeout(firstFrameTimer)
  keepAliveTimer = tickTimer = firstFrameTimer = undefined
  resizeObserver?.disconnect()
  resizeObserver = undefined
  if (target.value && sessionId.value) {
    await invokeHub('PararEspelho', target.value.id, sessionId.value).catch(() => { /* a aba do agente para sozinha em 30 s */ })
  }
  destroyPlayer()
  pending.clear()
  sessionId.value = null
  target.value = null
  liveState.value = 'waiting'
  lastActivity.value = null
}

async function switchAgent() {
  await stop()
  error.value = ''
  step.value = 'pick'
  await nextTick()
  searchRef.value?.focus()
  await loadAgents()
}

function close() {
  if (document.fullscreenElement) void document.exitFullscreen()
  screenMirrorOpen.value = false
}

// ── quadros ─────────────────────────────────────────────────────────────────────────────────

function onChunk(c: MirrorChunk) {
  if (c.sessionId !== sessionId.value || c.agentId.toLowerCase() !== target.value?.id.toLowerCase()) return
  const key = `${c.tabId}:${c.seq}`
  const batch = pending.get(key) ?? { parts: new Array(c.total), got: 0 }
  if (batch.parts[c.index] === undefined) {
    batch.parts[c.index] = c.chunk
    batch.got++
  }
  pending.set(key, batch)
  if (batch.got < batch.parts.length) {
    if (pending.size > 300) pending.delete(pending.keys().next().value!) // lote que nunca completou
    return
  }
  pending.delete(key)
  unpackMirrorEvents(batch.parts.join('')).then(events => apply(c.tabId, events)).catch(() => { /* lote corrompido: o próximo checkout corrige */ })
}

function onState(s: MirrorState) {
  if (s.sessionId !== sessionId.value) return
  if (s.state === 'marcacoes_limpas') {
    board.apply({ op: 'clear' })
    myMarks.value = []
    showMarksNotice('As marcações sumiram: o agente trocou de página ou redimensionou a janela.')
    schedulePaint()
    return
  }
  if (s.state === 'marcacoes_ocultas' || s.state === 'marcacoes_visiveis') {
    agentHidMarks.value = s.state === 'marcacoes_ocultas'
    return
  }
  if (s.state === 'oculta') liveState.value = 'hidden'
  else if (s.state === 'encerrado') liveState.value = 'ended'
  else liveState.value = replayer ? 'live' : 'waiting'
}

function apply(tab: string, events: eventWithTime[]) {
  for (const e of events) {
    // Meta abre toda foto inteira (início da gravação, aba que voltou, "Atualizar"): recomeça o player nela.
    if (e.type === EventType.Meta) {
      createPlayer(e, tab)
      continue
    }
    if (!replayer || tab !== currentTab) continue
    replayer.addEvent({ ...e, timestamp: e.timestamp + offset } as eventWithTime)
  }
  if (!replayer) return
  lastActivity.value = Date.now()
  if (liveState.value === 'waiting' || liveState.value === 'ended') liveState.value = 'live'
}

function createPlayer(meta: eventWithTime, tab: string) {
  destroyPlayer()
  if (!playerRoot.value) return
  // Relógio do agente ≠ relógio daqui: alinha pelo momento em que a foto chegou.
  offset = Date.now() - meta.timestamp
  currentTab = tab
  const data = meta.data as { width?: number; height?: number }
  viewport = { width: data.width ?? 1280, height: data.height ?? 720 }
  replayer = new Replayer([], {
    root: playerRoot.value,
    liveMode: true,
    mouseTail: false,
    showWarning: false,
    triggerFocus: false,
    UNSAFE_replayCanvas: false,
  })
  replayer.on('resize', (d: any) => {
    if (d?.width && d?.height) viewport = { width: d.width, height: d.height }
    fit()
  })
  replayer.startLive(Date.now() - 300)
  replayer.addEvent({ ...meta, timestamp: meta.timestamp + offset } as eventWithTime)
  hasPlayer.value = true
  if (liveState.value !== 'hidden') liveState.value = 'live'
  void nextTick(fit)
}

function destroyPlayer() {
  try { replayer?.destroy() } catch { /* já destruído */ }
  replayer = null
  currentTab = null
  hasPlayer.value = false
  if (playerRoot.value) playerRoot.value.innerHTML = ''
}

/** A tela do agente tem o tamanho da janela dele: encolhe (ou amplia até 1,5x) pra caber no palco. */
function fit() {
  const stage = stageRef.value
  const root = playerRoot.value
  const wrapper = root?.querySelector<HTMLElement>('.replayer-wrapper')
  if (!stage || !root || !wrapper || !viewport.width || !viewport.height) return
  const scale = Math.min(stage.clientWidth / viewport.width, stage.clientHeight / viewport.height, 1.5)
  wrapper.style.transform = `scale(${scale})`
  wrapper.style.transformOrigin = 'top left'
  root.style.width = `${Math.floor(viewport.width * scale)}px`
  root.style.height = `${Math.floor(viewport.height * scale)}px`
  schedulePaint()
}

// ── print e tela cheia ──────────────────────────────────────────────────────────────────────

async function takePrint() {
  const doc = playerRoot.value?.querySelector('iframe')?.contentDocument
  if (!doc?.documentElement || printing.value) return
  printing.value = true
  error.value = ''
  try {
    const canvas = await html2canvas(doc.documentElement, {
      width: viewport.width,
      height: viewport.height,
      windowWidth: viewport.width,
      windowHeight: viewport.height,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    })
    const stamp = new Date().toLocaleString('pt-BR').replace(/[/:]/g, '-').replace(', ', '_')
    const link = document.createElement('a')
    link.href = canvas.toDataURL('image/png')
    link.download = `tela-${(target.value ? fullName(target.value) : 'agente').replace(/\s+/g, '-').toLowerCase()}-${stamp}.png`
    link.click()
  } catch {
    error.value = 'Não deu pra gerar o print desta tela. Use Win+Shift+S enquanto isso.'
  } finally {
    printing.value = false
  }
}

function toggleFullscreen() {
  if (document.fullscreenElement) void document.exitFullscreen()
  else void stageRef.value?.requestFullscreen?.()
}

// ── marcações (lápis, seta, texto, ponteiro) ─────────────────────────────────────────────────
// O que você marca vai pro hub (MarcarEspelho) e aparece por cima da tela do agente; aqui o mesmo quadro desenha
// por cima da imagem. Posições em proporção da tela dele (0 a 1). Lápis/seta somem em 10 s; texto fica até limpar.

type Tool = 'none' | 'pen' | 'arrow' | 'text' | 'pointer'
const TOOLS: { id: Exclude<Tool, 'none'>; title: string; icon: string }[] = [
  { id: 'pen', title: 'Lápis — desenha na tela do agente (some em 10 s)', icon: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>' },
  { id: 'arrow', title: 'Seta — arraste do ponto inicial até onde apontar (some em 10 s)', icon: '<line x1="5" y1="19" x2="19" y2="5"/><polyline points="9 5 19 5 19 15"/>' },
  { id: 'text', title: 'Texto — clique onde quer o recado (fica até limpar)', icon: '<polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/>' },
  { id: 'pointer', title: 'Apontar — o agente vê um ponto seguindo o seu mouse', icon: '<circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="8" stroke-dasharray="3 3"/>' },
]
const SEND_EVERY_MS = 50

const tool = ref<Tool>('none')
const color = ref<string>(MARK_COLORS[0])
const markCanvas = ref<HTMLCanvasElement | null>(null)
const textInput = ref<HTMLTextAreaElement | null>(null)
const myMarks = ref<string[]>([])
const agentHidMarks = ref(false)
const marksNotice = ref('')
const textDraft = ref<{ x: number; y: number; left: number; top: number; value: string } | null>(null)
const board = new MarkBoard()
let paintFrame = 0
let annotated = false
let drawing: { id: string; kind: 'stroke' | 'arrow'; start: Point } | null = null
let pendingPoints: Point[] = []
let sendTimer: ReturnType<typeof setInterval> | undefined
let lastPointerSent = 0
let noticeTimer: ReturnType<typeof setTimeout> | undefined

function pickTool(t: Tool) {
  tool.value = tool.value === t ? 'none' : t
  textDraft.value = null
}

function showMarksNotice(text: string) {
  marksNotice.value = text
  if (noticeTimer) clearTimeout(noticeTimer)
  noticeTimer = setTimeout(() => { marksNotice.value = '' }, 5_000)
}

function paintMarks() {
  paintFrame = 0
  const canvas = markCanvas.value
  if (!canvas || !canvas.clientWidth) return
  const animating = board.prune()
  const ctx = fitCanvas(canvas, canvas.clientWidth, canvas.clientHeight)
  if (ctx) drawMarks(ctx, canvas.clientWidth, canvas.clientHeight, board)
  if (animating) paintFrame = requestAnimationFrame(paintMarks)
}

function schedulePaint() {
  if (!paintFrame) paintFrame = requestAnimationFrame(paintMarks)
}

/** Aplica aqui e manda pro agente. A 1ª marcação da sessão fica registrada na auditoria (só que houve). */
function sendMark(msg: MarkMessage) {
  if (!target.value || !sessionId.value) return
  board.apply(msg)
  schedulePaint()
  if (!annotated) {
    annotated = true
    invokeHub('RegistrarMarcacaoEspelho', target.value.id, sessionId.value).catch(() => { /* só auditoria */ })
  }
  invokeHub('MarcarEspelho', target.value.id, sessionId.value, JSON.stringify(msg)).catch(() => { /* reconecta e segue */ })
}

function flushPoints(done = false) {
  if (!drawing || drawing.kind !== 'stroke') return
  if (!pendingPoints.length && !done) return
  const points = pendingPoints
  pendingPoints = []
  if (!target.value || !sessionId.value) return
  const msg: MarkMessage = { op: 'append', id: drawing.id, points, done }
  invokeHub('MarcarEspelho', target.value.id, sessionId.value, JSON.stringify(msg)).catch(() => { /* segue */ })
}

function at(e: PointerEvent): Point {
  const rect = markCanvas.value!.getBoundingClientRect()
  return [Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)), Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height))]
}

function onMarkDown(e: PointerEvent) {
  if (!hasPlayer.value || tool.value === 'none' || tool.value === 'pointer') return
  const p = at(e)
  if (tool.value === 'text') {
    e.preventDefault()
    const rect = markCanvas.value!.getBoundingClientRect()
    textDraft.value = { x: p[0], y: p[1], left: p[0] * rect.width, top: p[1] * rect.height, value: '' }
    void nextTick(() => textInput.value?.focus())
    return
  }
  markCanvas.value!.setPointerCapture(e.pointerId)
  const id = newMarkId()
  drawing = { id, kind: tool.value === 'pen' ? 'stroke' : 'arrow', start: p }
  if (drawing.kind === 'stroke') {
    sendMark({ op: 'add', mark: { id, kind: 'stroke', color: color.value, width: 4, points: [p], done: false } })
    myMarks.value = [...myMarks.value, id]
    sendTimer = setInterval(() => flushPoints(), SEND_EVERY_MS)
  } else {
    // Seta: prévia só aqui; vai pro agente pronta, no soltar.
    board.apply({ op: 'add', mark: { id, kind: 'arrow', color: color.value, width: 4, points: [p, p], done: false } })
    schedulePaint()
  }
}

function onMarkMove(e: PointerEvent) {
  if (tool.value === 'pointer' && hasPlayer.value) {
    const now = Date.now()
    if (now - lastPointerSent < SEND_EVERY_MS) return
    lastPointerSent = now
    const [x, y] = at(e)
    sendMark({ op: 'pointer', x, y, color: color.value })
    return
  }
  if (!drawing) return
  const p = at(e)
  if (drawing.kind === 'stroke') {
    board.apply({ op: 'append', id: drawing.id, points: [p] })
    pendingPoints.push(p)
  } else {
    const m = board.marks.get(drawing.id)
    if (m) m.points = [drawing.start, p]
  }
  schedulePaint()
}

function onMarkUp(e: PointerEvent) {
  if (!drawing) return
  const d = drawing
  if (d.kind === 'stroke') {
    if (sendTimer) clearInterval(sendTimer)
    sendTimer = undefined
    flushPoints(true)
    board.apply({ op: 'append', id: d.id, points: [], done: true })
  } else {
    const end = at(e)
    board.apply({ op: 'undo', id: d.id })
    // Clique sem arrastar não vira seta.
    if (Math.hypot(end[0] - d.start[0], end[1] - d.start[1]) > 0.01) {
      sendMark({ op: 'add', mark: { id: d.id, kind: 'arrow', color: color.value, width: 4, points: [d.start, end], done: true } })
      myMarks.value = [...myMarks.value, d.id]
    }
  }
  drawing = null
  schedulePaint()
}

function onMarkLeave(e: PointerEvent) {
  if (drawing && !markCanvas.value?.hasPointerCapture(e.pointerId)) onMarkUp(e)
}

function commitText() {
  const draft = textDraft.value
  textDraft.value = null
  const text = draft?.value.trim()
  if (!draft || !text) return
  const id = newMarkId()
  sendMark({ op: 'add', mark: { id, kind: 'text', color: color.value, width: 4, points: [[draft.x, draft.y]], text, done: true } })
  myMarks.value = [...myMarks.value, id]
}

function undoMark() {
  const id = myMarks.value[myMarks.value.length - 1]
  if (!id) return
  myMarks.value = myMarks.value.slice(0, -1)
  sendMark({ op: 'undo', id })
}

function clearMarks() {
  myMarks.value = []
  sendMark({ op: 'clear' })
}

/** Fim da sessão (parar, trocar de agente): zera tudo daqui. */
function resetMarks() {
  if (sendTimer) clearInterval(sendTimer)
  sendTimer = undefined
  drawing = null
  pendingPoints = []
  board.apply({ op: 'clear' })
  myMarks.value = []
  annotated = false
  agentHidMarks.value = false
  marksNotice.value = ''
  textDraft.value = null
  tool.value = 'none'
  schedulePaint()
}

function onKeydown(e: KeyboardEvent) {
  if (screenMirrorOpen.value && e.key === 'Escape' && !document.fullscreenElement) {
    if (textDraft.value) { textDraft.value = null; return }
    if (tool.value !== 'none') { tool.value = 'none'; return }
    close()
  }
}

const offs: (() => void)[] = []
onMounted(() => {
  offs.push(onHubEvent('QuadrosEspelho', onChunk), onHubEvent('EstadoEspelho', onState))
  window.addEventListener('keydown', onKeydown)
})
onUnmounted(() => {
  offs.forEach(off => off())
  window.removeEventListener('keydown', onKeydown)
  void stop()
})
</script>

<style scoped>
.sm-overlay {
  position: fixed; inset: 0; z-index: 9100;
  background: rgba(0, 0, 0, 0.55); backdrop-filter: blur(4px);
  display: flex; align-items: flex-start; justify-content: center; padding: 8vh 16px 16px;
}
.sm-box {
  width: 100%; max-width: 560px; max-height: 84vh;
  background: #1a2332; color: #e5e7eb;
  border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 14px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
  display: flex; flex-direction: column; overflow: hidden;
}
.sm-box--watch { max-width: min(1500px, 96vw); height: 88vh; max-height: none; margin-top: -5vh; }

.sm-head { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
.sm-head--watch { flex-wrap: wrap; }
.sm-head-icon { width: 32px; height: 32px; border-radius: 9px; display: grid; place-items: center; background: rgba(167, 139, 250, 0.16); color: #c4b5fd; flex: none; }
.sm-head-text { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.sm-head-text strong { font-size: 14px; color: #fff; }
.sm-head-text > span { font-size: 12px; color: rgba(255, 255, 255, 0.55); }
.sm-kbd { font-size: 11px; padding: 2px 7px; border-radius: 5px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.12); color: rgba(255, 255, 255, 0.6); font-family: inherit; }
.sm-icon-btn { width: 28px; height: 28px; border-radius: 7px; display: grid; place-items: center; background: transparent; border: 0; color: rgba(255, 255, 255, 0.6); cursor: pointer; }
.sm-icon-btn:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }

.sm-search-row { display: flex; align-items: center; gap: 10px; padding: 10px 16px; color: rgba(255, 255, 255, 0.45); border-bottom: 1px solid rgba(255, 255, 255, 0.06); }
.sm-search { flex: 1; background: transparent; border: 0; outline: none; color: #fff; font-size: 14px; font-family: inherit; }
.sm-search::placeholder { color: rgba(255, 255, 255, 0.35); }

.sm-list { overflow-y: auto; padding: 6px; flex: 1; min-height: 120px; }
.sm-state { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 28px; font-size: 13px; color: rgba(255, 255, 255, 0.5); }
.sm-agent { width: 100%; display: flex; align-items: center; gap: 12px; padding: 9px 10px; border-radius: 9px; background: transparent; border: 0; color: inherit; text-align: left; cursor: pointer; font-family: inherit; }
.sm-agent--focus { background: rgba(255, 255, 255, 0.07); }
.sm-agent:disabled { opacity: 0.6; cursor: wait; }
.sm-avatar { position: relative; width: 34px; height: 34px; border-radius: 50%; background: #334155; display: grid; place-items: center; font-size: 12px; font-weight: 600; color: #fff; flex: none; }
.sm-avatar img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
.sm-dot { position: absolute; right: -1px; bottom: -1px; width: 10px; height: 10px; border-radius: 50%; border: 2px solid #1a2332; }
.sm-dot--on { background: #22c55e; }
.sm-dot--off { background: #64748b; }
.sm-agent-body { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.sm-agent-name { font-size: 13.5px; font-weight: 600; color: #fff; }
.sm-agent-sub { font-size: 12px; color: rgba(255, 255, 255, 0.5); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sm-agent-status { font-size: 11.5px; color: rgba(255, 255, 255, 0.4); }
.sm-agent-status--on { color: #4ade80; }

.sm-error { margin: 0; padding: 8px 16px; font-size: 12.5px; color: #fca5a5; background: rgba(239, 68, 68, 0.1); }
.sm-error--watch { border-top: 1px solid rgba(239, 68, 68, 0.2); }
.sm-foot { padding: 10px 16px 12px; font-size: 11.5px; line-height: 1.5; color: rgba(255, 255, 255, 0.45); border-top: 1px solid rgba(255, 255, 255, 0.06); }

.sm-back { display: inline-flex; align-items: center; gap: 4px; height: 30px; padding: 0 10px; border-radius: 8px; background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.1); color: rgba(255, 255, 255, 0.8); font-size: 12.5px; cursor: pointer; font-family: inherit; }
.sm-back:hover { background: rgba(255, 255, 255, 0.1); }
.sm-live { display: inline-flex; align-items: center; gap: 5px; font-weight: 600; }
.sm-live::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.sm-live--live { color: #f87171; }
.sm-live--live::before { animation: sm-pulse 1.4s ease-in-out infinite; }
.sm-live--waiting { color: #fbbf24; }
.sm-live--hidden, .sm-live--ended { color: #94a3b8; }
@keyframes sm-pulse { 50% { opacity: 0.3; } }

.sm-actions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.sm-btn { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 11px; border-radius: 8px; background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.1); color: rgba(255, 255, 255, 0.85); font-size: 12.5px; cursor: pointer; font-family: inherit; }
.sm-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.12); }
.sm-btn:disabled { opacity: 0.45; cursor: default; }
.sm-btn--stop { background: #b91c1c; border-color: #b91c1c; color: #fff; font-weight: 600; }
.sm-btn--stop:hover:not(:disabled) { background: #991b1b; }

.sm-stage { position: relative; flex: 1; min-height: 0; background: #0b1220; display: flex; align-items: center; justify-content: center; overflow: hidden; }
.sm-board { position: relative; }
.sm-marks { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; touch-action: none; }
.sm-marks--pen, .sm-marks--arrow { pointer-events: auto; cursor: crosshair; }
.sm-marks--text { pointer-events: auto; cursor: text; }
.sm-marks--pointer { pointer-events: auto; cursor: none; }
.sm-text-input { position: absolute; z-index: 2; width: 240px; max-width: 60%; padding: 6px 8px; border: 2px solid; border-radius: 8px; font: 600 13px Poppins, system-ui, sans-serif; resize: none; outline: none; background: #fff; color: #111827; box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3); }
.sm-marks-notice { position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%); padding: 6px 12px; border-radius: 999px; background: rgba(15, 23, 42, 0.9); color: #fde68a; font-size: 12px; border: 1px solid rgba(253, 230, 138, 0.3); max-width: calc(100% - 24px); text-align: center; }
.sm-tools { display: inline-flex; align-items: center; gap: 3px; padding: 2px 4px; border-radius: 9px; background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); }
.sm-tool { width: 28px; height: 26px; display: grid; place-items: center; border-radius: 6px; border: 0; background: transparent; color: rgba(255, 255, 255, 0.75); cursor: pointer; }
.sm-tool:hover:not(:disabled) { background: rgba(255, 255, 255, 0.1); color: #fff; }
.sm-tool.is-on { background: rgba(239, 68, 68, 0.25); color: #fff; }
.sm-tool:disabled { opacity: 0.35; cursor: default; }
.sm-color { width: 16px; height: 16px; border-radius: 50%; border: 2px solid transparent; cursor: pointer; padding: 0; margin: 0 1px; }
.sm-color.is-on { border-color: #fff; box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.4); }
.sm-tools-sep { width: 1px; height: 16px; background: rgba(255, 255, 255, 0.15); margin: 0 3px; }
.sm-player { position: relative; overflow: hidden; background: #fff; box-shadow: 0 8px 30px rgba(0, 0, 0, 0.45); }
.sm-player:empty { display: none; }
.sm-player :deep(.replayer-wrapper) { position: absolute; top: 0; left: 0; }
.sm-player :deep(iframe) { border: 0; pointer-events: none; background: #fff; }
.sm-stage-msg { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 24px; text-align: center; color: rgba(255, 255, 255, 0.7); font-size: 13px; }
.sm-stage-msg strong { font-size: 15px; color: #fff; }
.sm-stage-banner { position: absolute; top: 10px; left: 50%; transform: translateX(-50%); padding: 6px 12px; border-radius: 999px; background: rgba(15, 23, 42, 0.85); color: #e2e8f0; font-size: 12px; border: 1px solid rgba(255, 255, 255, 0.12); }

.sm-spinner { width: 14px; height: 14px; border-radius: 50%; border: 2px solid rgba(255, 255, 255, 0.2); border-top-color: #c4b5fd; animation: sm-spin 0.8s linear infinite; display: inline-block; }
.sm-spinner--big { width: 28px; height: 28px; border-width: 3px; margin-bottom: 4px; }
@keyframes sm-spin { to { transform: rotate(360deg); } }

.sm-fade-enter-active, .sm-fade-leave-active { transition: opacity 0.15s ease; }
.sm-fade-enter-from, .sm-fade-leave-to { opacity: 0; }

@media (max-width: 640px) {
  .sm-overlay { padding: 0; }
  .sm-box, .sm-box--watch { max-width: none; height: 100vh; max-height: none; border-radius: 0; margin: 0; }
}
</style>
